import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  DocumentArrowDownIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

import { bulkOrderService, BulkOrder } from '../../services/bulkOrderService';
import { sanitizeDisplayedFailureReason } from '../../utils/notificationManager';
import { useSocket } from '../../hooks/useSocket';

import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import Badge from '../../components/common/Badge';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ProgressBar from '../../components/common/ProgressBar';

const BulkOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { socket } = useSocket();
  
  const [bulkOrder, setBulkOrder] = useState<BulkOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [verifyingPaystack, setVerifyingPaystack] = useState(false);

  // Load bulk order details
  useEffect(() => {
    if (id) {
      loadBulkOrder(id);
    }
  }, [id]);

  // Handle Paystack redirect back to /bulk-orders/:id?reference=...
  useEffect(() => {
    if (!id) return;
    if (verifyingPaystack) return;

    const params = new URLSearchParams(location.search || '');
    const reference = String(params.get('reference') || '').trim();
    if (!reference) return;

    const run = async () => {
      try {
        setVerifyingPaystack(true);
        toast.loading('Verifying payment...', { id: 'bulk-paystack-verify' });
        await bulkOrderService.verifyPaystackPayment(reference);
        toast.success('Payment verified.', { id: 'bulk-paystack-verify' });
        await loadBulkOrder(id);
      } catch (error: any) {
        toast.error(error?.response?.data?.message || 'Payment verification failed', { id: 'bulk-paystack-verify' });
      } finally {
        setVerifyingPaystack(false);

        // Clean up the URL so refresh doesn't re-verify
        try {
          params.delete('reference');
          const newSearch = params.toString();
          const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`;
          window.history.replaceState({}, '', newUrl);
        } catch (e) {
        }
      }
    };

    run();
  }, [id, location.search, verifyingPaystack]);

  // Listen for real-time updates
  useEffect(() => {
    if (!socket || !bulkOrder) return;

    const handleBulkOrderUpdate = (data: any) => {
      if (data.bulkOrderId === bulkOrder._id) {
        loadBulkOrder(bulkOrder._id);
        
        // Show notifications for status changes
        if (data.status === 'completed') {
          toast.success('Order placed successfully.');
        } else if (data.status === 'partially_completed') {
          toast.error('Bulk order partially completed');
        } else if (data.status === 'failed') {
          toast.error('Bulk order failed');
        }
      }
    };

    socket.on('bulkOrderUpdate', handleBulkOrderUpdate);

    return () => {
      socket.off('bulkOrderUpdate', handleBulkOrderUpdate);
    };
  }, [socket, bulkOrder]);

  const loadBulkOrder = async (bulkOrderId: string) => {
    try {
      setLoading(true);
      const order = await bulkOrderService.getBulkOrder(bulkOrderId);
      setBulkOrder(order);
    } catch (error: any) {

      toast.error(error.response?.data?.message || 'Failed to load bulk order');
    } finally {
      setLoading(false);
    }
  };

  const refreshOrder = async () => {
    if (!bulkOrder) return;
    
    try {
      setRefreshing(true);
      await loadBulkOrder(bulkOrder._id);
      toast.success('Order refreshed');
    } catch (error) {
      toast.error('Failed to refresh order');
    } finally {
      setRefreshing(false);
    }
  };

  const exportToCSV = () => {
    if (!bulkOrder) return;
    
    const csvContent = bulkOrderService.exportToCSV(bulkOrder);
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk-order-${bulkOrder.orderNumber}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast.success('Order exported to CSV');
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'failed':
      case 'partially_completed':
      case 'cancelled':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'pending_payment':
      case 'pending_admin_processing':
      case 'draft':
      case 'processing':
        return <ClockIcon className="h-5 w-5 text-blue-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getRecipientStatusIcon = (status: string) => {
    switch (status) {
      case 'processed':
      case 'completed':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />;
      case 'processing':
        return <ClockIcon className="h-4 w-4 text-blue-500" />;
      default:
        return <ClockIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!bulkOrder) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Bulk Order Not Found
          </h2>
          <Button onClick={() => navigate('/bulk-orders')}>
            Back to Bulk Orders
          </Button>
        </div>
      </div>
    );
  }

  const statusInfo = bulkOrderService.formatStatus(bulkOrder.status);
  const progressInfo = bulkOrderService.getStatusProgress(bulkOrder);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6 sm:py-8">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/bulk-orders')}
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white break-words">
                  {bulkOrder.orderNumber}
                </h1>
                <div className="flex flex-wrap items-center mt-2 gap-2">
                  <Badge variant={statusInfo.variant as any}>
                    {getStatusIcon(bulkOrder.status)}
                    <span className="ml-2">{statusInfo.text}</span>
                  </Badge>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Created {new Date(bulkOrder.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                onClick={refreshOrder}
                disabled={refreshing}
              >
                {refreshing ? (
                  <LoadingSpinner size="sm" className="mr-2" />
                ) : (
                  <ArrowPathIcon className="h-4 w-4 mr-2" />
                )}
                Refresh
              </Button>
              <Button
                variant="outline"
                onClick={exportToCSV}
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Left Column - Order Details */}
          <div className="lg:col-span-1 space-y-6">
            {/* Order Summary */}
            <Card className="p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Order Summary
              </h2>
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Package
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white break-words">
                    {bulkOrder.dataPackage?.name || 'Multiple Packages'}
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Network
                  </span>
                  <Badge variant="primary" size="sm">
                    {bulkOrder.network.toUpperCase()}
                  </Badge>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Total Recipients
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {bulkOrder.totalRecipients}
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Unit Price
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    GHS {bulkOrder.pricing.unitPrice.toFixed(2)}
                  </span>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Total Amount
                    </span>
                    <span className="text-lg font-bold text-primary-600">
                      GHS {bulkOrder.pricing.totalPrice.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Progress */}
            <Card className="p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Processing Progress
              </h2>
              <div className="space-y-4">
                <ProgressBar
                  value={progressInfo.percentage}
                  className="mb-4"
                />
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-green-600">
                      {progressInfo.successful}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Successful
                    </p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">
                      {progressInfo.failed}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Failed
                    </p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-600">
                      {progressInfo.pending}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Pending
                    </p>
                  </div>
                </div>
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Success Rate
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {bulkOrder.successRate}%
                    </span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Payment Info */}
            <Card className="p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Payment Information
              </h2>
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Method
                  </span>
                  <Badge variant="secondary" size="sm">
                    {(bulkOrder.payment?.method || 'N/A').toUpperCase()}
                  </Badge>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Status
                  </span>
                  <Badge 
                    variant={(bulkOrder.payment?.status || 'pending') === 'completed' ? 'success' : 'warning'} 
                    size="sm"
                  >
                    {(bulkOrder.payment?.status || 'pending').toUpperCase()}
                  </Badge>
                </div>
                {bulkOrder.payment?.reference && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 min-w-0">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Reference
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white min-w-0 break-all">
                      {bulkOrder.payment.reference}
                    </span>
                  </div>
                )}
                {bulkOrder.notes && (
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                      Notes
                    </p>
                    <p className="text-sm text-gray-900 dark:text-white break-words">
                      {bulkOrder.notes}
                    </p>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Right Column - Recipients */}
          <div className="lg:col-span-2">
            <Card className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Recipients ({bulkOrder.recipients.length})
                </h2>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {progressInfo.percentage}% Complete
                  </span>
                </div>
              </div>

              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {bulkOrder.recipients.map((recipient: any, index: number) => (
                  <div
                    key={index}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  >
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700">
                        {getRecipientStatusIcon(recipient.status || 'pending')}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white break-all">
                          {recipient.phone}
                        </p>
                        <div className="flex items-center space-x-2 mt-1">
                          <Badge variant="secondary" size="sm" className="text-[10px] px-1.5 py-0.5">
                            {recipient.network.toUpperCase()}
                          </Badge>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {recipient.status || 'pending'}
                          </span>
                          {recipient.processedAt && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(recipient.processedAt).toLocaleTimeString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-left sm:text-right">
                      {recipient.status === 'completed' && (
                        <span className="text-sm text-green-600 font-medium">
                          Success
                        </span>
                      )}
                      {recipient.status === 'failed' && (
                        <div>
                          <span className="text-sm text-red-600 font-medium">
                            Failed
                          </span>
                          {recipient.failureReason && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-words">
                              {sanitizeDisplayedFailureReason(recipient.failureReason, false)}
                            </p>
                          )}
                        </div>
                      )}
                      {recipient.status === 'processing' && (
                        <span className="text-sm text-blue-600 font-medium">
                          Processing...
                        </span>
                      )}
                      {!recipient.status && (
                        <span className="text-sm text-gray-600 font-medium">
                          Pending
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkOrderDetailPage;
